<template>
  <div class="inquiry-modal" v-if="visible" @click.self="closeModal">
    <div class="modal-content">
      <!-- 标题 -->
      <div class="modal-header">
        <h3>询盘订单</h3>
        <span class="close-btn" @click="closeModal">&times;</span>
      </div>

      <!-- 订单列表 -->
      <div class="order-list">
        <!-- 当前页订单 -->
        <div class="order-item" v-for="(order, index) in paginatedOrders" :key="index">
          <div class="order-field">
            <span class="field-label">商品名称：</span>
            <span class="field-value ellipsis-3">{{ order.commodityName }}</span>
          </div>

          <div class="order-field">
            <span class="field-label">商品规格：</span>
            <span class="field-value">{{ order.specifications || '无' }}</span>
          </div>

          <div class="order-row">
            <div class="order-field half-width">
              <span class="field-label">购买数量：</span>
              <span class="field-value">{{ order.quantity }}</span>
            </div>
            <div class="order-field half-width">
              <span class="field-label">单价：</span>
              <span class="field-value">{{ order.unitPrice }}</span>
            </div>
          </div>

          <div class="order-row">
            <div class="order-field half-width">
              <span class="field-label">支付状态：</span>
              <span class="field-value">
                {{
                order.status == 0
                ? "未支付"
                : order.status == 1
                ? "已支付"
                : order.status == 2
                ? "已发货"
                : "已完成"
                }}
              </span>
            </div>
            <div class="order-field half-width">
              <span class="field-label">总价：</span>
              <span class="field-value">{{ order.totalPrice }}</span>
            </div>
          </div>

          <div class="order-field">
            <span class="field-label">回件商家邮箱：</span>
            <span class="field-value">{{ order.businessEmail }}</span>
          </div>

          <div class="order-field">
            <span class="field-label">是否发送邮箱：</span>
            <span class="field-value">{{ order.isEmail ? '已发送' : '未发送' }}</span>
          </div>

          <!-- 收款方式切换按钮 -->
          <div class="toggle-payment-btn" @click="togglePayment(order.id)">
            {{ showPaymentMap[order.id] ? '收起收款方式' : '展开收款方式' }}
            <span class="arrow-icon" :class="{ 'rotate': showPaymentMap[order.id] }">▼</span>
          </div>

          <!-- 收款方式 -->
          <div class="payment-method" v-show="showPaymentMap[order.id]">
            <h4>收款方式</h4>

            <!-- 银行转账 -->
            <div v-if="order.collectionType === 2" class="bank-info">
              <div class="payment-field">
                <span class="field-label">受益人姓名：</span>
                <span class="field-value">{{ order.beneficiaryName }}</span>
              </div>
              <div class="payment-field">
                <span class="field-label">受益人地址：</span>
                <span class="field-value">{{ order.beneficiaryAddress }}</span>
              </div>
              <div class="payment-field">
                <span class="field-label">受益人账号：</span>
                <span class="field-value">{{ order.beneficiaryAccNo }}</span>
              </div>
              <div class="payment-field">
                <span class="field-label">银行地址：</span>
                <span class="field-value">{{ order.addressOfBank }}</span>
              </div>
              <div class="payment-field">
                <span class="field-label">SWIFT码：</span>
                <span class="field-value">{{ order.swiftCode }}</span>
              </div>
              <div class="payment-field">
                <span class="field-label">邮政编码：</span>
                <span class="field-value">{{ order.postCode }}</span>
              </div>
              <div class="payment-field">
                <span class="field-label">电话：</span>
                <span class="field-value">{{ order.tel }}</span>
              </div>
              <div v-if="order.customDefinition" class="payment-field">
                <span class="field-label">自定义字段：</span>
                <span class="field-value">{{ order.customDefinition }}</span>
              </div>
            </div>

            <!-- 收款码 -->
            <div v-else-if="order.collectionType === 1" class="qrcode-info">
              <div class="qrcode-image-container">
                <img :src="order.collectionQrcode" alt="收款码" class="qrcode-image">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页控件 -->
      <div class="pagination" v-if="orderData.length > itemsPerPage">
        <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">
          上一页
        </button>
        <span class="page-info">
          第 {{ currentPage }} 页 / 共 {{ totalPages }} 页
        </span>
        <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">
          下一页
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'InquiryOrderModal',
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      orderData: {
        type: Array,
        default: () => [
          {
            id: 1, // 必须要有唯一id
            commodityName: '',
            specifications: '',
            quantity: 0,
            unitPrice: 0,
            status: '未支付',
            totalPrice: 0,
            businessEmail: '',
            isEmail: false,
            collectionType: 2, // 1: 收款码, 2: 银行转账
            beneficiaryName: '',
            beneficiaryAddress: '',
            beneficiaryAccNo: '',
            addressOfBank: '',
            swiftCode: '',
            postCode: '',
            tel: '',
            customDefinition: '',
            collectionQrcode: ''
          }
        ],
        validator: value => {
          // 验证每个订单必须有id
          return value.every(order => order.hasOwnProperty('id'))
        }
      },
      itemsPerPage: {
        type: Number,
        default: 1
      }
    },
    data() {
      return {
        currentPage: 1,
        showPaymentMap: {} // 使用对象存储展开状态，key为订单id
      }
    },
    computed: {
      totalPages() {
        return Math.ceil(this.orderData.length / this.itemsPerPage)
      },
      paginatedOrders() {
        const start = (this.currentPage - 1) * this.itemsPerPage
        const end = start + this.itemsPerPage
        return this.orderData.slice(start, end)
      }
    },
    watch: {
      orderData: {
        immediate: true,
        handler(newVal) {
          // 初始化收款方式显示状态
          const newMap = {}
          newVal.forEach(order => {
            newMap[order.id] = this.showPaymentMap[order.id] || false
          })
          this.showPaymentMap = newMap
        }
      }
    },
    methods: {
      closeModal() {
        this.$emit('close')
        this.currentPage = 1
      },
      togglePayment(orderId) {
        this.$set(this.showPaymentMap, orderId, !this.showPaymentMap[orderId])
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--
        }
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++
        }
      }
    }
  }
</script>

<style scoped>
  .inquiry-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 10px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
  }

  .modal-header {
    padding: 15px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .close-btn {
    font-size: 24px;
    cursor: pointer;
    color: #999;
    flex-shrink: 0;
  }

  .order-list {
    padding: 15px;
    flex: 1;
    overflow: hidden;
  }

  .order-item {
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #f5f5f5;
    word-break: break-word;
    overflow: hidden;
  }

  .order-item:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .order-field {
    margin-bottom: 10px;
    display: flex;
    overflow: hidden;
  }

  .ellipsis-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
  }

  .order-row {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
  }

  .half-width {
    width: 48%;
    overflow: hidden;
  }

  .field-label {
    color: #666;
    min-width: 80px;
    flex-shrink: 0;
  }

  .field-value {
    color: #333;
    word-break: break-word;
    flex: 1;
    overflow: hidden;
  }

  .toggle-payment-btn {
    margin-top: 10px;
    padding: 8px 0;
    color: #1890ff;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  .arrow-icon {
    margin-left: 5px;
    transition: transform 0.3s;
    font-size: 12px;
  }

  .arrow-icon.rotate {
    transform: rotate(180deg);
  }

  .payment-method {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #eee;
  }

  .payment-method h4 {
    margin: 0 0 15px 0;
    font-size: 16px;
    color: #333;
  }

  .bank-info .payment-field {
    margin-bottom: 8px;
    display: flex;
    overflow: hidden;
  }

  .qrcode-image-container {
    display: flex;
    justify-content: center;
    margin-top: 15px;
  }

  .qrcode-image {
    max-width: 100%;
    max-height: 200px;
    border: 1px solid #eee;
  }

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 15px;
    border-top: 1px solid #eee;
    position: sticky;
    bottom: 0;
    background-color: #fff;
    flex-wrap: wrap;
  }

  .page-btn {
    padding: 8px 15px;
    margin: 0 5px;
    border: 1px solid #ddd;
    background-color: #f9f9f9;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
  }

  .page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .page-info {
    margin: 0 10px;
    font-size: 14px;
    color: #666;
    white-space: nowrap;
  }

  /* 防止内容溢出导致横向滚动 */
  * {
    max-width: 100%;
  }
</style>